import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { DownloadIcon, ArrowSquareOutIcon } from '@phosphor-icons/react';

interface AttachmentViewerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attachmentUrl: string;
  title?: string;
}

function getFileType(
  url: string,
): 'pdf' | 'image' | 'document' | 'spreadsheet' | 'unknown' {
  const extension = url.split('.').pop()?.toLowerCase() || '';

  if (extension === 'pdf') return 'pdf';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) return 'image';
  if (['doc', 'docx'].includes(extension)) return 'document';
  if (['xls', 'xlsx', 'csv'].includes(extension)) return 'spreadsheet';

  return 'unknown';
}

function getFileName(url: string): string {
  try {
    const pathname = new URL(url).pathname;
    const segments = pathname.split('/');
    return segments[segments.length - 1] || 'attachment';
  } catch {
    return 'attachment';
  }
}

export function AttachmentViewerModal({
  open,
  onOpenChange,
  attachmentUrl,
  title = 'View Attachment',
}: AttachmentViewerModalProps) {
  const fileType = getFileType(attachmentUrl);
  const fileName = getFileName(attachmentUrl);

  function handleDownload() {
    window.open(attachmentUrl, '_blank');
  }

  function renderContent() {
    switch (fileType) {
      case 'pdf':
        return (
          <iframe
            src={attachmentUrl}
            className="w-full h-[70vh] border-0 rounded-md"
            title={fileName}
          />
        );

      case 'image':
        return (
          <div className="flex items-center justify-center bg-muted/30 rounded-md p-4">
            <img
              src={attachmentUrl}
              alt={fileName}
              className="max-w-full max-h-[70vh] object-contain rounded-md"
            />
          </div>
        );

      case 'document':
      case 'spreadsheet':
      case 'unknown':
      default:
        // For documents and spreadsheets, show download link
        return (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="text-center">
              <p className="text-lg font-medium">{fileName}</p>
              <p className="text-sm text-muted-foreground mt-1">
                This file type cannot be previewed directly.
              </p>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleDownload}>
                <DownloadIcon className="h-4 w-4 mr-2" />
                Download File
              </Button>
              <Button variant="outline" asChild>
                <a
                  href={attachmentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ArrowSquareOutIcon className="h-4 w-4 mr-2" />
                  Open in New Tab
                </a>
              </Button>
            </div>
          </div>
        );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{title}</span>
            {(fileType === 'pdf' || fileType === 'image') && (
              <Button variant="outline" size="sm" onClick={handleDownload}>
                <DownloadIcon className="h-4 w-4 mr-2" />
                Download
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-auto">{renderContent()}</div>
      </DialogContent>
    </Dialog>
  );
}
